import { Navigate } from 'react-router-dom';
import { ArrowRight, Bot, MessagesSquare, Scale, Workflow } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { AssistantDemo } from '@/components/mcp/AssistantDemo';
import { CopyField, PromptCard } from '@/components/mcp/CopyField';
import { CheckList, CtaPanel, FaqSection, HeroActions, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, StepFlow } from '@/components/page/PageKit';
import { ASSISTANT_INTEGRATIONS, ASSISTANT_REVIEWED, getAssistant, type AssistantIntegration } from '@/data/assistantIntegrations';
import { MCP_SERVER_URL } from '@/data/mcpContent';
import { APP_URL } from '@/data/marketingContent';
import { faqSchema } from '@/seo/schemas';
import { useSEO } from '@/hooks/useSEO';

function AssistantPage({ assistant }: { assistant: AssistantIntegration }) {
  const { name } = assistant;
  const url = `https://beatview.ai/integrations/${assistant.slug}`;
  const shortAnswer = `Beatview connects to ${name} through its MCP server at ${MCP_SERVER_URL}, on every Beatview plan. Once you approve the connection, ${name} can open and publish jobs, list and compare candidates with their AI resume and interview scores, show the pipeline and — after you confirm — move candidates or send AI interview invitations. Screening and interviews keep running inside Beatview whether or not the chat is open.`;
  useSEO({ title: assistant.seoTitle, description: assistant.description, canonicalUrl: url });

  return (
    <PageShell>
      <PageStructuredData name={`Beatview for ${name}`} description={assistant.description} url={url} breadcrumbs={[{ name: 'Integrations', url: 'https://beatview.ai/integrations' }, { name }]} />
      <StructuredData data={faqSchema(assistant.faqs)} />
      <PageHero breadcrumbs={[{ name: 'Integrations', href: '/integrations' }, { name }]} eyebrow={`Beatview + ${name}`} title={<>Run your hiring <span className="bv-hero-highlight">from {name}</span></>} description={`${assistant.about} Connect Beatview and ${name} can work with the candidates your agent has already screened and interviewed.`} visual={<AssistantDemo compact initialClient={name} />}>
        <HeroActions primaryLabel="Start for free" primaryHref={APP_URL} secondary={{ label: 'How the MCP server works', href: '/mcp' }} />
        <CopyField value={MCP_SERVER_URL} label="Server" className="mt-6 max-w-md" />
      </PageHero>

      <Section tone="white" className="py-14 lg:py-16" narrow>
        <InShort>{shortAnswer}</InShort>
      </Section>

      <Section tone="dark" labelledBy="setup-title">
        <SectionHeading tone="dark" id="setup-title" eyebrow="Set up" title={`Connect Beatview to ${name} in four steps`} />
        <div className="mt-16"><StepFlow tone="dark" steps={assistant.steps} /></div>
      </Section>

      <Section tone="base" labelledBy="requirements-title">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="min-w-0">
            <SectionHeading id="requirements-title" eyebrow="Before you connect" title={`What ${name} needs`} description={`These requirements are set by ${assistant.maker}, not Beatview. On the Beatview side, the MCP server is included on every plan.`} />
            <Reveal delay={0.1}>
              <a href={assistant.source.url} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-bv-blue hover:underline">{assistant.source.label} <ArrowRight className="h-3.5 w-3.5" /></a>
              <p className="mt-2 text-[13px] text-bv-mute">Checked {ASSISTANT_REVIEWED}.</p>
            </Reveal>
          </div>
          <Reveal delay={0.05} className="min-w-0">
            <CheckList items={assistant.requirements} />
            {assistant.command && (
              <div className="mt-7">
                <p className="text-[14px] font-semibold text-bv-ink">{assistant.command.label}</p>
                <CopyField value={assistant.command.value} className="mt-2" />
              </div>
            )}
          </Reveal>
        </div>
      </Section>

      <Section tone="wash" labelledBy="fit-title">
        <SectionHeading id="fit-title" eyebrow="How they fit" title={`What stays in Beatview, and what you do from ${name}`} />
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-bv-blue-line bg-white p-7 sm:p-8">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-bv-blue-wash text-bv-blue"><Workflow className="h-5 w-5" /></span>
              <h3 className="mt-5 font-display text-xl font-semibold text-bv-ink">Keeps running in Beatview</h3>
              <CheckList className="mt-5" items={['Resume screening against your role criteria, as applications arrive', 'Stage rules that send AI interview invitations', 'Interview recordings — viewable only by signed-in members of your account', 'Your career page, branding and plan limits']} />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full rounded-3xl bg-bv-ink p-7 text-white shadow-bv-deep sm:p-8">
              <span className="bv-btn-gradient flex h-11 w-11 items-center justify-center rounded-xl"><MessagesSquare className="h-5 w-5" /></span>
              <h3 className="mt-5 font-display text-xl font-semibold">What you do from {name}</h3>
              <CheckList tone="dark" className="mt-5" items={assistant.strengths} />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section tone="white" labelledBy="prompts-title">
        <SectionHeading id="prompts-title" eyebrow="Prompts to try" title={`Six things to ask ${name}`} description="Click to copy, then paste into a chat with Beatview connected." />
        <div className="mt-12 grid gap-3 md:grid-cols-2">
          {assistant.prompts.map((prompt, i) => <Reveal key={prompt} delay={(i % 2) * 0.05}><PromptCard prompt={prompt} /></Reveal>)}
        </div>
      </Section>

      <FaqSection title={`Beatview and ${name}: common questions`} items={assistant.faqs} idPrefix={`${assistant.slug}-faq`} tone="base" />
      <RelatedLinks
        eyebrow="Other assistants"
        title="Beatview works with any MCP-compatible assistant"
        items={[
          ...ASSISTANT_INTEGRATIONS.filter(a => a.slug !== assistant.slug).map(a => ({ title: `Beatview + ${a.name}`, body: a.about, href: `/integrations/${a.slug}`, icon: Bot })),
          { title: 'The Beatview MCP server', body: 'Every action, the prompt library and the safety limits in one place.', href: '/mcp', icon: Workflow },
          { title: 'Recruiting MCP servers compared', body: 'Which recruiting tools work with AI assistants, and what each one exposes.', href: '/guides/recruiting-mcp-servers', icon: Scale },
        ]}
      />
      <CtaPanel eyebrow={`Using ${name}?`} title={<>Ask {name} to open <span className="bv-hero-highlight">your next role.</span></>} description={`Create a free Beatview account, connect ${name} and start with one active role.`} bullets={['Included on every plan', 'Asks before it acts', 'Recruiters keep every decision']} secondary={{ label: 'Book a demo', href: '/contact' }} />
    </PageShell>
  );
}

export function AssistantIntegrationPage({ slug }: { slug: string }) {
  const assistant = getAssistant(slug);
  return assistant ? <AssistantPage assistant={assistant} /> : <Navigate to="/integrations" replace />;
}

export const ClaudeIntegration = () => <AssistantIntegrationPage slug="claude" />;
export const ChatGptIntegration = () => <AssistantIntegrationPage slug="chatgpt" />;
export const GeminiIntegration = () => <AssistantIntegrationPage slug="gemini" />;
